import React, { Dispatch, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DialogHeader from '../_parts/DialogHeader';
import { useDispatch, useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../../redux/store';
import {
    closeUploadAdditionalDialog,
    getAdditionalDocumentTypes,
    resetLoading,
} from '../../../../redux/project/documents/sliceProjectUploadAdditionalDocuments';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { updateSelectedDocuments } from '../../../../redux/project/documents/sliceProjectSelectedDocuments';
import { useEnqueueSnackbar } from '../../../../hooks/useEnqueueSnackbar';
import { ToastSeverity } from '../../Toast/Toast';
import DialogFrame from '../_parts/DialogFrame';
import {
    getAdditionalDocs,
    setDocumentsToExpandNoteIds,
} from '../../../../redux/project/documents/sliceProjectAdditionalDocuments';
import { DialogContent } from '@mui/material';
import { ContentLayout } from '../_parts/ContentLayout';
import { removeProjectDocumentsInfoItem } from '../../../../redux/project/documents/sliceProjectDocuments';
import Documents from '../_parts/Documents';
import UploadAdditionalDocumentContent from './_parts/UploadAdditionalDocumentContent';
import UploadAdditionalDocumentDialogActions from './_parts/UploadAdditionalDocumentDialogActions';
import { fetchViewDataAndDocuments } from '../../../../util/ProjectUtils';
import {
    fetchImportDocTypes,
    fetchImportViewData,
    fetchImportViews,
} from '../../../../util/ImportUtils';

const initialMainState: IUploadAdditionalDocumentMainState = {
    selectedDocTypeName: '',
    selectedDocTypeVariant: '',
    subject: '',
    date: dayjs().valueOf(),
};

const initialErrorState: IUploadAdditionalDocumentErrorState = {
    selectedDocTypeVariant: false,
    subject: false,
    date: false,
    file: false,
};

export default () => {
    const { t } = useTranslation('translations', { keyPrefix: 'uploadAdditionalDocumentDialog' });
    const dispatch: Dispatch<unknown> = useDispatch();
    const params = useParams();
    const { replicaId, areaName } = params;

    const enqueueSnackbar = useEnqueueSnackbar();

    const {
        isDialogOpen,
        isUploadCompleted,
        fileInfo,
        loading: loadingDocumentTypeOptions,
    } = useSelector((_store: IAppReduxStore) => _store.projectUploadAdditionalDocuments);

    const { data: dialogSelectedDocuments, loading: loadingInfoDocs } = useSelector(
        (_store: IAppReduxStore) => _store.projectDocuments.info,
    );
    const projectDocuments = useSelector((_store: IAppReduxStore) => _store.projectDocuments);
    const importDocuments = useSelector((_store: IAppReduxStore) => _store.importDocuments.data);
    const fields = useSelector((store: IAppReduxStore) => store.projectAdditionalDocs.fields.data);
    const fieldSchema = useSelector(
        (store: IAppReduxStore) => store.projectAdditionalDocs.schema.data,
    );
    const allAdditionalDocuments = useSelector(
        (store: IAppReduxStore) => store.projectAdditionalDocs.documents.data,
    );
    const documentsToExpandNoteIds = useSelector(
        (store: IAppReduxStore) => store.projectAdditionalDocs.documentsToExpandNoteIds,
    );
    const additionalDocError = useSelector(
        (store: IAppReduxStore) => store.projectAdditionalDocs.documents.error,
    );

    const areAllAdditionalDocumentsFetched = useMemo(() => {
        return (
            documentsToExpandNoteIds.length > 0 &&
            documentsToExpandNoteIds.every(noteId => allAdditionalDocuments[noteId])
        );
    }, [documentsToExpandNoteIds, allAdditionalDocuments]);

    const expandAdditionalDocs = () => {
        const documentIds = dialogSelectedDocuments.map(x => x.universalId);
        const allDocuments = areaName === 'import' ? importDocuments : projectDocuments.data;
        const documents = allDocuments.filter(x => documentIds.includes(x.UniversalID));

        const noteIds = documents.map(x => x.NoteId);
        dispatch(setDocumentsToExpandNoteIds(noteIds));

        for (const doc of documents) {
            dispatch(
                getAdditionalDocs({
                    replicaId,
                    universalId: doc.UniversalID,
                    noteId: doc.NoteId,
                    fields: [].concat(
                        ...fieldSchema.map(uf => fields.find(x => x.field === uf)?.fields ?? []),
                    ),
                }),
            );
        }
    };

    useEffect(() => {
        if (additionalDocError) {
            onClose();
        }
    }, [additionalDocError]);

    useEffect(() => {
        if (isDialogOpen) {
            dispatch(getAdditionalDocumentTypes({ replicaId }));
        }
    }, [isDialogOpen]);

    useEffect(() => {
        if (fileInfo) {
            setShowErrorState({
                ...showErrorState,
                file: false,
                subject: false,
            });
            setMainState({
                ...mainState,
                subject: (mainState.subject ?? fileInfo.fileName) || fileInfo.fileName,
            });
        }
    }, [fileInfo]);

    const handleClosingDialog = () => {
        dispatch(
            updateSelectedDocuments({
                replicaId,
                areaName,
                documentIds: [],
            }),
        );
        onClose();
        enqueueSnackbar(t('toastSuccess'), {
            severity: ToastSeverity.Success,
        });
    };

    useEffect(() => {
        if (isUploadCompleted) {
            if (areaName === 'import') {
                fetchImportViews(replicaId);
                fetchImportViewData(replicaId);
                fetchImportDocTypes(replicaId);
            } else {
                fetchViewDataAndDocuments(params);
            }
            expandAdditionalDocs();
        }
    }, [isUploadCompleted]);

    useEffect(() => {
        if (areAllAdditionalDocumentsFetched) {
            handleClosingDialog();
        }
    }, [allAdditionalDocuments]);

    const [mainState, setMainState] =
        useState<IUploadAdditionalDocumentMainState>(initialMainState);
    const [showErrorState, setShowErrorState] =
        useState<IUploadAdditionalDocumentErrorState>(initialErrorState);

    const handleChange = ({ field, value }: { field: string; value: string | number }) => {
        setShowErrorState({ ...showErrorState, [field]: false });
        setMainState(prevState => ({
            ...prevState,
            [field]: value,
        }));
    };

    const resetToInitialState = () => {
        setMainState(initialMainState);
        setShowErrorState(initialErrorState);
    };

    const onClose = () => {
        resetToInitialState();
        dispatch(resetLoading());
        dispatch(setDocumentsToExpandNoteIds([]));
        dispatch(closeUploadAdditionalDialog());
    };

    const removeDocument = (universalId: string) => {
        dispatch(removeProjectDocumentsInfoItem({ universalId }));
    };

    return (
        <DialogFrame open={isDialogOpen}>
            <DialogHeader onClose={onClose} dialogTitle={t('dialogTitle')} />
            <DialogContent sx={{ pt: 0, overflowX: 'hidden' }}>
                <ContentLayout
                    loading={loadingInfoDocs || loadingDocumentTypeOptions}
                    documentSelection={
                        <Documents
                            removeDocument={removeDocument}
                            documents={dialogSelectedDocuments}
                        />
                    }
                    main={
                        <UploadAdditionalDocumentContent
                            mainState={mainState}
                            handleChange={handleChange}
                            showErrorState={showErrorState}
                            setShowErrorState={setShowErrorState}
                        />
                    }
                />
            </DialogContent>

            <UploadAdditionalDocumentDialogActions
                setShowErrorState={setShowErrorState}
                mainState={mainState}
                onClose={onClose}
            />
        </DialogFrame>
    );
};

export interface IUploadAdditionalDocumentMainState {
    selectedDocTypeName: string;
    selectedDocTypeVariant: string;
    subject: string;
    date: number;
}

export interface IUploadAdditionalDocumentErrorState {
    selectedDocTypeVariant: boolean;
    subject: boolean;
    date: boolean;
    file: boolean;
}
